import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
    ReactReduxContext,
} from 'react-redux';
import {
    matchRoutes,
    renderRoutes,
} from 'react-router-config';
import { onCLS } from 'web-vitals/attribution';

import { replayFeatureFlagsToRum } from 'wanderu-analytics';
import { CHECKOUT_URL_SEGMENT } from 'wanderu-app-booking';
import {
    GDPRBanner,
    GDPRBannerEvents,
} from 'wanderu-components';
import {
    fireSplitTestEvent,
} from 'wanderu-ducks-split-test';
import {
    i18nCoordinator,
} from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import {
    SnowplowService,
} from 'wanderu-service-analytics';
import {
    isHomePage,
    isLandingPage,
    isLocalStorageAvailable,
} from 'wanderu-utils';

import GlobalSiteBanner from '../../components/GlobalSiteBanner';
import localesConfig from '../../config/baseApp.locales';
import {
    injectAccountDucks,
} from '../../userDuckSwap';
import Header from '../Header/Header';

import 'wanderu-css/app.scss';

import styles from './App.scss';

const logger = Logger.getLogger('App');

const LOCAL_STORAGE_KEY = 'GDPRStatus';

function shouldShowGDPRBanner() {
    if (!isLocalStorageAvailable()) {
        return true;
    }

    try {
        const gdprStatus = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_KEY)) || {};
        return gdprStatus.showBanner !== false;
    } catch {
        logger.warn(`Unable to parse local storage value for ${LOCAL_STORAGE_KEY}`);
        return true;
    }
}

function permanentlyHideGDPRBanner() {
    if (!isLocalStorageAvailable()) {
        return;
    }

    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ showBanner: false }));
}

function trackEvent() {
    const date = new Date();
    GDPRBannerEvents.bannerRendered({ date });
}

// Compute view name out of routeMatches
function computeViewName(routeMatches) {
    let viewName = '';

    routeMatches.forEach(routeMatch => {
        const path = routeMatch?.route?.path;
        // Skip pathless routes
        if (!path) {
            return;
        }

        if (path.startsWith('/')) {
            // Handle absolute child route paths
            viewName = path;
        } else {
            // Handle route paths ending with "/"
            viewName += viewName.endsWith('/')
                ? path
                : `/${path}`;
        }
    });

    return viewName || '/';
}

function startDatadogView(route, pathname) {
    const routeMatches = matchRoutes(route.routes, pathname);
    const viewName = computeViewName(routeMatches);

    window?.DD_RUM?.startView({ name: viewName });
    // A new view starts with an empty flag context, so re-tag it.
    replayFeatureFlagsToRum();
}

export class App extends React.Component {
    static contextType = ReactReduxContext;

    accountsLoaded = false;

    static async clientFetchData(i18nData) {
        const {
            currency,
            locale,
        } = i18nData;

        return i18nCoordinator.initI18nLibraries(locale, currency);
    }

    constructor(props) {
        super(props);

        this.state = {
            menuOpen: false,
            showBanner: false,
            showDropdown: true,
        };
    }

    shouldLoadAccountCode(pathname) {
        return !isLandingPage(pathname) && !this.accountsLoaded;
    }

    componentDidMount() {
        const {
            location,
            setCurrency,
            setLocale,
            setLocaleAndCurrency,
            setSupportedCurrencies,
            setSupportedLocales,
            splitTestAssignedEvent,
        } = this.props;

        if (location.pathname.indexOf('/stations') > -1) {
            this.setState({ showDropdown: false });
        }

        i18nCoordinator.registerListeners({
            onCurrencyChange: (currency) => {
                if (!currency) {
                    return; // What to do here?
                }

                setCurrency(currency);
            },
            onLocaleChange: (locale, _, optionalCurrency) => {
                if (!locale) {
                    return undefined; // What to do here?
                }

                if (optionalCurrency) {
                    return setLocaleAndCurrency({ currency: optionalCurrency, locale });
                }

                return setLocale(locale);
            },
            onSupportedCurrenciesChange: setSupportedCurrencies,
            onSupportedLocalesChange: setSupportedLocales,
        });
        i18nCoordinator.registerTranslatableApp({
            ...localesConfig,
            translationFetchFn: (locale) => {
                return import(`../../translations/baseApp/${locale}.po`);
            },
        });
        this.props.fetchBrowserMeta(window.navigator.userAgent);

        if (this.shouldLoadAccountCode(location.pathname)) {
            this.accountsLoaded = true;

            window.requestIdleCallback(() => {
                this.loadAccount();
            }, { timeout: 5000 });
        }

        if (shouldShowGDPRBanner()) {
            trackEvent();
            this.setState(() => ({
                showBanner: true,
            }));
        }

        fireSplitTestEvent(splitTestAssignedEvent);
        Logger.config.addMetadata('splitTests', () => splitTestAssignedEvent.property.treatments);
        startDatadogView(this.props.route, location.pathname);

        try {
            onCLS((metric) => {
                const value = metric?.value ?? 0;
                const {
                    largestShiftSource,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    largestShiftEntry: _largestShiftEntry,
                    ...restAttribution
                } = metric?.attribution ?? {};

                const payload = {
                    attribution: {
                        largestShiftSource: {
                            className: largestShiftSource?.node?.className,
                            id: largestShiftSource?.node?.id,
                        },
                        ...restAttribution,
                    },
                    navigationType: metric?.navigationType,
                    value,
                };

                if (value > 0.1) {
                    logger.warn('web-vitals CLS threshold exceeded', payload);
                } else {
                    logger.debug('web-vitals CLS threshold not exceeded', payload);
                }
            });
        } catch (ex) {
            logger.warn('web-vitals failed to register onCLS', ex);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            history,
            location,
            match,
        } = this.props;

        const { locale } = match.params;
        const { locale: prevLocale } = prevProps.match.params;
        const localeChanged = locale !== prevLocale;

        const prevPath = prevProps.location.pathname;
        const { pathname: path } = location;
        const locationChanged = history.action !== 'REPLACE' && path !== prevPath;

        if (!locationChanged) {
            return;
        }

        this.onRouteChanged();
        if (!localeChanged) {
            this.hideGDPRBanner();
        }

        if (this.shouldLoadAccountCode(location.pathname)) {
            this.accountsLoaded = true;

            window.requestIdleCallback(() => this.loadAccount(), { timeout: 5000 });
        }
    }

    async loadAccount() {
        let reducers;
        try {
            const promise = import('wanderu-ducks-account');
            const {
                default: reducerMap,
                logInAction,
                logOutAction,
                userInfoSelectors,
            } = await promise;

            reducers = Object.keys(reducerMap);

            this.context.store.inject(reducerMap);
            injectAccountDucks(userInfoSelectors, {
                ...logOutAction,
                ...logInAction,
            });

            this.props.performAutoLogIn();
        } catch (ex) {
            logger.error('loadAccount', ex, reducers);
        }
    }

    onRouteChanged() {
        SnowplowService.trackPageView();
        startDatadogView(this.props.route, this.props.location.pathname);
    }

    hideGDPRBanner() {
        this.setState(() => ({
            showBanner: false,
        }));
        permanentlyHideGDPRBanner();
    }

    // eslint-disable-next-line class-methods-use-this
    componentWillUnmount() {
        i18nCoordinator.unregisterListeners();
        i18nCoordinator.unregisterTranslatableApp(localesConfig.namespace);
    }

    onMenuToggle = () => {
        this.setState((oldState) => ({ menuOpen: !oldState.menuOpen }));
    };

    renderGDPRBanner = () => {
        const {
            inEuropeanUnion,
            locale,
        } = this.props;

        if (!this.state.showBanner) {
            return undefined;
        }

        if (!inEuropeanUnion) {
            return undefined;
        }

        return (
            <GDPRBanner
                locale={locale}
                onClose={this.hideGDPRBanner}
            />
        );
    };

    render() {
        const {
            globalBanner,
            globalBannerVisible,
            locale,
            location,
            onLogOut,
            route,
        } = this.props;
        const { pathname } = location;
        const { menuOpen } = this.state;

        const isCheckout = pathname.replace(/\//g, '') === `${locale}${CHECKOUT_URL_SEGMENT}`;

        const isSEOPage = isLandingPage(pathname) || isHomePage(pathname);
        const showBanner = globalBannerVisible && isSEOPage;
        const isStickyHeader = isSEOPage;

        return (
            <div
                className={classNames(
                    styles.container,
                    { [styles.noScroll]: menuOpen },
                )}
            >
                {!!globalBanner && showBanner && (
                    <GlobalSiteBanner
                        message={globalBanner.message}
                        type={globalBanner.type}
                        url={globalBanner.url}
                    />
                )}
                <Header
                    globalBannerVisible={globalBannerVisible}
                    isLandingPage={isLandingPage(pathname)}
                    isLoggedIn={this.props.isLoggedIn}
                    isSticky={isStickyHeader}
                    localeSwitcherVisible={!isCheckout}
                    navLinksVisible={!isCheckout}
                    onLogOut={onLogOut}
                    onMenuToggle={this.onMenuToggle}
                    showDropdown={this.state.showDropdown}
                    userFirstName={this.props.userFirstName}
                />
                {renderRoutes(route.routes)}
                {this.renderGDPRBanner()}
            </div>
        );
    }
}

App.propTypes = {
    fetchBrowserMeta: PropTypes.func,
    globalBanner: PropTypes.shape({
        message: PropTypes.string,
        type: PropTypes.string,
        url: PropTypes.string,
    }),
    globalBannerVisible: PropTypes.bool,
    history: PropTypes.shape({
        action: PropTypes.string,
        push: PropTypes.func,
        replace: PropTypes.func,
    }),
    inEuropeanUnion: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    locale: PropTypes.string,
    location: PropTypes.shape({
        pathname: PropTypes.string,
        search: PropTypes.string,
    }),
    match: PropTypes.shape({
        params: PropTypes.shape({
            locale: PropTypes.string,
        }),
    }),
    onLogOut: PropTypes.func,
    performAutoLogIn: PropTypes.func,
    route: PropTypes.shape({
        routes: PropTypes.array,
    }),
    setCurrency: PropTypes.func,
    setLocale: PropTypes.func,
    setLocaleAndCurrency: PropTypes.func,
    setSupportedCurrencies: PropTypes.func,
    setSupportedLocales: PropTypes.func,
    splitTestAssignedEvent: PropTypes.shape({
        action: PropTypes.string,
        category: PropTypes.string,
        property: PropTypes.shape({
            treatments: PropTypes.array,
            visitorId: PropTypes.string,
        }),
    }),
    userFirstName: PropTypes.string,
};
